/** Shelf cards for systems and books: the cover with its gauge, then the facts that identify it. */
import { Link } from 'react-router'
import { BookCheck } from 'lucide-react'
import { Cover } from './Cover'
import { categoryLabel, plural } from '../lib/format'
import { SERIES_KIND_LABELS } from '../lib/labels'
import type { BookCard as BookCardData, SeriesCard as SeriesCardData, SystemCard as SystemCardData } from '../lib/types'

/** A card's owned marker. The card's own label says "owned", so this is decoration. */
function Owned() {
  return (
    <span className="card-owned" aria-hidden="true">
      <BookCheck size={13} /> Owned
    </span>
  )
}

/**
 * A red M pill beside an explicit record's year or edition, so mature titles stand out. A card's
 * own label says "mature content", so there it is decoration; `labelled` names it where nothing
 * else does (a search result).
 */
export function Mature({ labelled = false }: { labelled?: boolean }) {
  return (
    <span
      className="mature-badge"
      title="Mature content"
      {...(labelled ? { role: 'img', 'aria-label': 'Mature content' } : { 'aria-hidden': true })}
    >
      M
    </span>
  )
}

export function SystemCard({ s }: { s: SystemCardData }) {
  const meta = [s.edition, s.year].filter(Boolean).join(' · ')
  const count = s.child_count > 0 ? plural(s.child_count, 'system') : plural(s.book_count, 'book')
  return (
    <Link
      to={`/systems/${s.id}`}
      className="card"
      aria-label={`${s.name}${s.is_explicit ? ', mature content' : ''}, ${count}, ${s.completeness}% complete${s.owned ? ', owned' : ''}`}
    >
      <Cover cover={s.cover} title={s.name} score={s.completeness} onePage={s.is_one_page} />
      <span className="card-title">{s.name}</span>
      <span className="card-meta">
        {(meta || s.is_explicit) && (
          <span className="card-meta-line">
            {meta}
            {s.is_explicit && <Mature />}
          </span>
        )}
        <span>{count}</span>
        {s.owned && <Owned />}
      </span>
    </Link>
  )
}

export function BookCard({ b, showSystems = true }: { b: BookCardData; showSystems?: boolean }) {
  const meta = [categoryLabel(b.category), b.year].filter(Boolean).join(' · ')
  return (
    <Link
      to={`/books/${b.id}`}
      className="card"
      aria-label={`${b.title}${b.is_explicit ? ', mature content' : ''}, ${meta}, ${b.completeness}% complete${b.owned ? ', owned' : ''}`}
    >
      <Cover cover={b.cover} title={b.title} score={b.completeness} withheld={b.cover_withheld} />
      <span className="card-title">
        {b.series_number && (
          <span className="card-number" aria-label={`Number ${b.series_number},`}>
            {b.series_number}
          </span>
        )}
        {b.title}
      </span>
      <span className="card-meta">
        <span className="card-meta-line">
          {meta}
          {b.is_explicit && <Mature />}
        </span>
        {showSystems && b.systems.length > 0 && (
          <span className="card-systems">{b.systems.map((s) => s.name).join(' + ')}</span>
        )}
        {b.variant_count > 0 && <span className="card-variant">+{plural(b.variant_count, 'variant')}</span>}
        {b.owned && <Owned />}
      </span>
    </Link>
  )
}

export function SystemShelf({ items, label }: { items: SystemCardData[]; label?: string }) {
  return (
    <ul className="shelf" aria-label={label}>
      {items.map((s) => (
        <li key={s.id}>
          <SystemCard s={s} />
        </li>
      ))}
    </ul>
  )
}

export function BookShelf({
  items,
  label,
  showSystems,
}: {
  items: BookCardData[]
  label?: string
  showSystems?: boolean
}) {
  return (
    <ul className="shelf" aria-label={label}>
      {items.map((b) => (
        <li key={b.id}>
          <BookCard b={b} showSystems={showSystems} />
        </li>
      ))}
    </ul>
  )
}

/** Placeholder shelf while a page loads: the grid's real shape, so nothing jumps. */
export function ShelfSkeleton({ count = 12 }: { count?: number }) {
  return (
    <ul className="shelf" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <li key={i}>
          <div className="card is-skeleton">
            <div className="cover cover-md" />
            <span className="skeleton-line" />
            <span className="skeleton-line is-short" />
          </div>
        </li>
      ))}
    </ul>
  )
}

export function SeriesCard({ s }: { s: SeriesCardData }) {
  const meta = [SERIES_KIND_LABELS[s.kind as keyof typeof SERIES_KIND_LABELS] ?? s.kind, s.systems[0]]
    .filter(Boolean)
    .join(' · ')
  const count = s.book_count ? plural(s.book_count, 'book') : ''
  return (
    <Link to={`/series/${s.id}`} className="card" aria-label={`${s.name}, ${meta}, ${s.completeness}% complete`}>
      <Cover cover={s.cover} title={s.name} score={s.completeness} />
      <span className="card-title">{s.name}</span>
      <span className="card-meta">
        <span>{meta}</span>
        {count && <span>{count}</span>}
      </span>
    </Link>
  )
}

export function SeriesShelf({ items, label }: { items: SeriesCardData[]; label?: string }) {
  return (
    <ul className="shelf" aria-label={label}>
      {items.map((s) => (
        <li key={s.id}>
          <SeriesCard s={s} />
        </li>
      ))}
    </ul>
  )
}
